import { MailCheck } from 'lucide-react';
import { useState } from 'react';
import type { BetterAuthClient } from '../../lib/betterAuthClient';
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';

export type EmailVerificationNoticeProps = {
  /** From `trpc.auth.me` — the notice renders nothing while true or still loading. */
  emailVerified: boolean | undefined;
  /** The portal's Better Auth client; the resend hits that app's own `/send-verification-email`. */
  authClient: BetterAuthClient;
  /** Where the verification link should land the user. Defaults to the current page. */
  callbackURL?: string;
  className?: string;
};

/**
 * Prompts a signed-in user to confirm their email address.
 *
 * Verification is not required to sign in or to use the product — it unlocks hosted AI, which is
 * the only part of the free tier with a real marginal cost (see `backend/src/utils/usage.ts`'s
 * `checkAiCreditBalance`). So this is a dismissible-in-spirit prompt rather than a wall, and it
 * says what verifying actually buys instead of nagging without a reason.
 *
 * Unopinionated per the shared-UI rule: it takes the verification flag and the auth client as
 * props and hardcodes no product, route, or portal.
 */
export function EmailVerificationNotice({
  emailVerified,
  authClient,
  callbackURL,
  className,
}: EmailVerificationNoticeProps) {
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  // Hooks must run before the early return below, so read the session unconditionally.
  const session = authClient.useSession();

  if (emailVerified !== false) return null;

  const resend = async () => {
    const email = session.data?.user.email;
    if (!email) {
      setStatus('error');
      return;
    }

    setStatus('sending');
    const { error } = await authClient.sendVerificationEmail({
      email,
      callbackURL: callbackURL ?? window.location.href,
    });
    setStatus(error ? 'error' : 'sent');
  };

  return (
    <Alert className={className}>
      <MailCheck className="h-4 w-4" />
      <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
        <span>
          {status === 'sent'
            ? 'Verification email sent. Check your inbox to turn on AI features.'
            : status === 'error'
              ? "We couldn't send that verification email. Try again in a moment."
              : 'Confirm your email address to turn on AI features. Everything else already works.'}
        </span>
        {status !== 'sent' && (
          <Button size="sm" variant="outline" onClick={resend} disabled={status === 'sending'}>
            {status === 'sending' ? 'Sending…' : 'Resend verification email'}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}
